import { QaIntegrationRequestDto, QaList } from '../../types';
import { useGetQaIntegrationList } from '../../hooks';
import React, { useEffect, useState } from 'react';
import { List, Modal, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import img from '../../assets/images/logo.png';
import { MapMarker, PhoneOutline, UserOutline } from '../../assets';
import { qaIntegrationMessage } from '../../messages';
import QaIntegrationDetail from './QaIntegrationDetail';

interface QaIntegrationListProps {
  requestData: QaIntegrationRequestDto;
  setRequestData: (requestData: QaIntegrationRequestDto) => void;
}

const formatDay = (dateStr?: string) => {
  return dateStr ? dayjs(dateStr).format('DD/MM/YYYY, HH:mm') : '';
};

const QaIntegrationList: React.FC<QaIntegrationListProps> = ({ requestData, setRequestData }) => {
  const { data, isLoading, refetch } = useGetQaIntegrationList(requestData);
  const [qaIntegrationList, setQaIntegrationList] = useState<QaList[]>([]);
  const [detailTicketId, setDetailTicketId] = useState<number>(0);

  useEffect(() => {
    setQaIntegrationList(data);
  }, [data]);

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0:
        return '#1564E8';
      case 1:
        return '#FF9500';
      case 2:
        return '#AF52DE';
      case 3:
      case 4:
        return '#02D8A0';
      default:
        return '#000';
    }
  };

  const [selectedQaTicket, setSelectedQaTicket] = useState<number>(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const openDetailModal = (id: number, ticketId: number) => {
    setIsOpenModal(true);
    setDetailTicketId(ticketId);
    setSelectedQaTicket(id);
  };

  const refreshAfterAction = () => {
    refetch();
    setIsOpenModal(false);
  };

  return (
    <div>
      <section>
        <List
          loading={isLoading}
          className='request_form_list list-item-hover-animation list-item-hover list-item gap-2 space-y-2 overflow-y-auto px-0 py-4'
          itemLayout='vertical'
          size='large'
          dataSource={qaIntegrationList}
          renderItem={(item, index) => {
            return (
              <List.Item
                onClick={() => openDetailModal(item?.id, item?.ticketId)}
                className='mb-2 flex justify-between rounded-lg border'
                key={index}
              >
                <div key={index} className='flex w-full justify-start gap-4 align-middle'>
                  <div className={'my-auto w-[12%] space-y-1.5'}>
                    <div>
                      <Typography.Text className='font-oswald text-[10px]/[12px] uppercase text-[#868D97]'>
                        {qaIntegrationMessage.evaluationDate}
                      </Typography.Text>
                      <br />
                      <Typography.Text className='font-oswald text-[14px]/[18px] text-[#141414]'>
                        {item?.evaluationDate ? formatDay(item?.evaluationDate) : ''}
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text className='font-oswald text-[10px]/[12px] uppercase text-[#868D97]'>
                        {qaIntegrationMessage.ticketCreatedDate}
                      </Typography.Text>
                      <br />
                      <Typography.Text className='font-oswald text-[14px]/[18px] text-[#141414]'>
                        {item?.ticketCreatedDate ? formatDay(item?.ticketCreatedDate) : ''}
                      </Typography.Text>
                    </div>
                  </div>

                  {/** --- GENERAL INFORMATION --- */}
                  <div
                    className={`flex w-[85%] justify-between border-l-[3px] pl-8`}
                    style={{ borderLeftColor: getStatusColor(item?.evaluationStatus) }}
                  >
                    <div className={'flex flex-col justify-start gap-6'}>
                      <div className='flex space-x-2'>
                        <Typography.Text className='my-auto text-[16px]/[20px] font-semibold text-[#1564E8]'>
                          {item?.id}
                        </Typography.Text>
                        <Typography.Text className='my-auto align-middle text-[16px]/[20px] font-semibold text-[#181818]'>
                          {item?.serviceTypeName
                            ? item?.serviceTypeName.split(' ').slice(0, 10).join(' ') +
                              (item?.serviceTypeName.split(' ').length > 10 ? '...' : '')
                            : 'Không có thông tin'}
                        </Typography.Text>
                      </div>

                      <div className='flex h-4 w-full flex-col space-y-2'>
                        <div className={'flex h-4 space-x-2'}>
                          <img className='h-4 w-4' src={img} alt='' />
                          <Typography.Text className='text-[14px]/[18px] text-[#4D5664]'>
                            {item?.customerCode}
                          </Typography.Text>
                          <Typography.Text className='relative bottom-3.5 text-2xl font-bold'>.</Typography.Text>
                          <UserOutline />
                          <Typography.Text className='top-1 text-[14px]/[18px] text-[#4D5664]'>
                            <Tooltip title={item?.customerRequestName || ''}>
                              {item?.customerRequestName
                                ? item?.customerRequestName.split(' ').slice(0, 5).join(' ') +
                                  (item?.customerRequestName.split(' ').length > 5 ? '...' : '')
                                : ''}
                            </Tooltip>
                          </Typography.Text>
                        </div>
                        <div className={'flex h-4 space-x-2'}>
                          <Typography.Text>
                            <PhoneOutline />
                          </Typography.Text>
                          <Typography.Text className='top-1 text-[14px]/[18px] text-[#4D5664]'>
                            {item?.customerRequestPhoneNumber}
                          </Typography.Text>
                          <Typography.Text className='relative bottom-3.5 text-2xl font-bold'>.</Typography.Text>
                          <Typography.Text>
                            <MapMarker />
                          </Typography.Text>
                          <Typography.Text className='top-1 text-[14px]/[18px] text-[#4D5664]'>
                            <Tooltip title={item?.customerRequestAddress || ''}>
                              {item?.customerRequestAddress
                                ? item?.customerRequestAddress.split(' ').slice(0, 10).join(' ') +
                                  (item?.customerRequestAddress.split(' ').length > 10 ? ' ...' : '')
                                : ''}
                            </Tooltip>
                          </Typography.Text>
                        </div>
                      </div>
                    </div>

                    <div className='flex flex-col justify-end gap-6 space-x-2'>
                      <Typography.Text
                        className='my-auto text-[14px]/[18px] font-normal'
                        style={{ color: getStatusColor(item?.evaluationStatus) }}
                      >
                        {item?.evaluationStatusDescription}
                      </Typography.Text>
                      <Typography.Text className='my-auto text-right text-[24px]/[32px] font-normal text-[#868D97]'>
                        <Typography.Text className='my-auto text-[24px]/[32px] font-semibold text-[#141414]'>
                          {item?.totalScore}
                        </Typography.Text>
                        /10
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />

        <Modal
          open={isOpenModal}
          onCancel={() => setIsOpenModal(false)}
          width={800}
          footer={null}
          className='ant-modal-body-p-none h-[95vh] w-fit rounded-xl bg-[#F5F4F6]'
          destroyOnClose
          centered
        >
          <QaIntegrationDetail
            evaluationId={selectedQaTicket}
            refreshAfterAction={refreshAfterAction}
            ticketId={detailTicketId}
          />
        </Modal>
      </section>
    </div>
  );
};

export default QaIntegrationList;
